import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User as UserIcon, Settings, LogOut } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu';
import { UserAvatar } from './UserAvatar';

interface UserAvatarMenuProps {
  user: User;
  onSignOut: () => void;
}

export function UserAvatarMenu({ user, onSignOut }: UserAvatarMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const fullName = user.user_metadata?.full_name || user.user_metadata?.name || null;
  const email = user.email || null;
  const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture || null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t('userMenu.openMenu')}
        >
          <UserAvatar
            avatarUrl={avatarUrl}
            fullName={fullName}
            email={email}
            size="sm"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            {fullName && <p className="text-sm font-medium leading-none">{fullName}</p>}
            {email && <p className="text-xs text-muted-foreground leading-none">{email}</p>}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={() => navigate('/app/account')}>
            <UserIcon />
            {t('userMenu.profile')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate('/app/settings')}>
            <Settings />
            {t('userMenu.settings')}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            navigate('/', { replace: true });
            // Delay sign-out until after the page exit animation (300ms)
            // so ProtectedRoute doesn't redirect during AnimatePresence exit
            setTimeout(() => onSignOut(), 350);
          }}
        >
          <LogOut />
          {t('userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
